import * as React from 'react';
import { v4 as uuid } from 'uuid';
import * as Sentry from '@sentry/nextjs';
import { useQuery } from '@apollo/client/react';
import { useMobileSearchModal } from '@components/mobile-search-modal';
import { FeatureKey, ListingsSearchDocument, ListingsSearchType, PlaceFindByIdDocument, PlaceSearchGeometryDocument, PropertyType } from '@gql';
// Note: ListingsSearchType import kept for use in constants only (always Map)
import { useDebounce, useToggle } from '@hooks';
import dayjs from '@utils/dayjs-setup';
import { guestDescription } from '@utils/guest-description';
import { isSSR } from '@utils/predicates';
import { useAnalytics } from '../use-analytics';
import type { Feature, Geometry, Point, SearchQueryParams } from '../search-context-utils';
import { buildSearchInitParams, buildSearchQueryVariables, calculateMapZoomFromRawTypes, DEFAULT_PLACE_ID, getDecodedSearchParams, searchPageSize } from '../search-context-utils';
import { SearchFiltersContextValue, SearchFiltersProvider } from './search-filters-context';
import { SearchMapContextValue, SearchMapProvider } from './search-map-context';
import { SearchResultsContextValue, SearchResultsProvider } from './search-results-context';
import { SearchUIContextValue, SearchUIProvider } from './search-ui-context';

// BHT-4019: window during which a burst of search-state changes is coalesced
// into a single browser-history entry, so the back button restores a coherent
// prior search rather than each intermediate setState.
const COMMIT_DEBOUNCE_MS = 400;

// After a Back/Forward, the Google Map programmatically re-applies the
// restored bbox and fires its own change event ~MAP_CHANGE_DEBOUNCE_MS (800ms
// in `use-search-map-state.ts`) later, which writes a slightly drifted bbox
// to the URL. Suppress new history pushes for long enough to cover that
// settle (map debounce + commit debounce + buffer) so the noise doesn't
// produce a phantom entry.
const POPSTATE_SETTLE_MS = 1800;
function syncStateToUrl(params: Record<string, string | undefined>) {
  if (isSSR) return;
  const queryParams = new URLSearchParams(window.location.search);
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') {
      queryParams.delete(key);
    } else {
      queryParams.set(key, value);
    }
  }
  history.replaceState(null, '', '?' + queryParams.toString());
}
type SearchQueryVariables = ReturnType<typeof buildSearchQueryVariables>;

// ~11m. The map's post-popstate bbox drift (see POPSTATE_SETTLE_MS) stays
// under this; a pan a guest makes does not.
const BOUNDS_DRIFT_TOLERANCE_DEGREES = 0.0001;
const BOUND_KEYS = ['northEastLat', 'northEastLng', 'southWestLat', 'southWestLng', 'centerLat', 'centerLng'] as const;

// Whether two variable sets are the same search for analytics: pagination is
// ignored and bounds may drift within tolerance.
function isSameSearchQuery(a: SearchQueryVariables, b: SearchQueryVariables): boolean {
  const withoutPagingOrBounds = ({
    cursorAfter: _cursorAfter,
    cursorBefore: _cursorBefore,
    pageSize: _pageSize,
    northEastLat: _northEastLat,
    northEastLng: _northEastLng,
    southWestLat: _southWestLat,
    southWestLng: _southWestLng,
    centerLat: _centerLat,
    centerLng: _centerLng,
    ...rest
  }: SearchQueryVariables) => JSON.stringify(rest);
  return withoutPagingOrBounds(a) === withoutPagingOrBounds(b) && BOUND_KEYS.every(key => {
    const [boundA, boundB] = [a[key], b[key]];
    if (boundA === undefined || boundB === undefined) {
      return boundA === boundB;
    }
    return Math.abs(boundA - boundB) < BOUNDS_DRIFT_TOLERANCE_DEGREES;
  });
}
export interface SearchProviderProps {
  accountId?: string;
  allFeatures: Feature[];
  children: React.ReactNode;
  initActiveFeatures?: string[];
  initPropertyTypes?: PropertyType[];
  initMinNumberOfBathrooms?: number;
  initMinNumberOfRooms?: number;
  initMinNumberOfBeds?: number;
  initMinPrice: number | null;
  initMaxPrice: number | null;
  initAdultCount?: number;
  initChildCount?: number;
  initInfantCount?: number;
  initPetCount?: number;
  initMapCoordinates?: Geometry;
  initMapZoom?: number;
  initCheckInDate: string | null;
  initCheckOutDate: string | null;
  placeId?: string;
  placeName?: string;
  cursorAfter: string | null;
  cursorBefore: string | null;
  pageSize?: number;
}
export const SearchProvider: React.FC<SearchProviderProps> = ({
  accountId,
  allFeatures,
  children,
  cursorAfter: _cursorAfter,
  cursorBefore: _cursorBefore,
  pageSize: _pageSize,
  initActiveFeatures = [],
  initAdultCount = 1,
  initCheckInDate,
  initCheckOutDate,
  initChildCount = 0,
  initInfantCount = 0,
  initMapCoordinates,
  initMapZoom,
  initMinNumberOfBathrooms = 0,
  initMinNumberOfRooms = 0,
  initMinNumberOfBeds = 0,
  initMinPrice,
  initMaxPrice,
  initPetCount = 0,
  initPropertyTypes = [],
  placeId,
  placeName
}) => {
  const {
    activateMobileSearchModal,
    deactivateMobileSearchModal,
    mobileSearchModalActive
  } = useMobileSearchModal();
  const {
    on: searchNavFormActive,
    setOn: activateSearchNavForm,
    setOff: deactivateSearchNavForm
  } = useToggle(false);
  const {
    on: featuresModalActive,
    setOn: activateFeaturesModal,
    setOff: deactivateFeaturesModal
  } = useToggle(false);
  const [mapViewportManuallyChanged, setMapViewportManuallyChanged] = React.useState(false);
  const markMapViewportAsManuallyChanged = React.useCallback(() => {
    setMapViewportManuallyChanged(true);
  }, []);

  // Search type is always Map — geo-only search
  const searchType = ListingsSearchType.Map;
  const [centerPoint, setCenterPoint] = React.useState<Point | undefined>(initMapCoordinates?.centerPoint);
  const [geoSearchBox, _setMapGeoSearchBox] = React.useState<Geometry | undefined>(initMapCoordinates);
  const [mapZoom, setMapZoom] = React.useState(initMapZoom);
  const setMapGeoSearchBox = React.useCallback((geometry: Geometry) => {
    syncStateToUrl({
      northEastLat: geometry.northEastPoint.lat.toString(),
      northEastLng: geometry.northEastPoint.lng.toString(),
      southWestLat: geometry.southWestPoint.lat.toString(),
      southWestLng: geometry.southWestPoint.lng.toString(),
      centerLat: geometry.centerPoint.lat.toString(),
      centerLng: geometry.centerPoint.lng.toString()
    });
    _setMapGeoSearchBox(geometry);
  }, []);
  const [cursors, setCursors] = React.useState<{
    cursorBefore: undefined | string;
    cursorAfter: undefined | string;
  }>({
    cursorAfter: _cursorAfter ? _cursorAfter : undefined,
    cursorBefore: _cursorBefore ? _cursorBefore : undefined
  });
  const [pageSize] = React.useState<number>(_pageSize || searchPageSize);
  const resetPagination = React.useCallback(() => {
    setCursors({
      cursorBefore: undefined,
      cursorAfter: undefined
    });
  }, []);
  const [resolvedPlaceName, setResolvedPlaceName] = React.useState<string | undefined>(placeName);

  // Whether we should resolve geometry before searching.
  // True when the URL has a placeId but no coordinates (e.g. user searched
  // from the homepage). Account searches use a global bounding box and don't
  // need geometry resolution.
  const shouldResolveGeometry = !initMapCoordinates && !accountId;
  const {
    data: placeByIdData,
    error: placeByIdError
  } = useQuery(PlaceFindByIdDocument, {
    variables: {
      id: placeId || ''
    },
    // Fire when we need raw place metadata OR when we need to resolve the place name
    skip: !placeId || !shouldResolveGeometry && !!placeName
  });
  const {
    data: placeSearchGeometryData,
    error: placeSearchGeometryError
  } = useQuery(PlaceSearchGeometryDocument, {
    variables: {
      id: placeId || ''
    },
    skip: !placeId || !shouldResolveGeometry
  });

  // Resolve place name from PlaceFindById response
  React.useEffect(() => {
    if (!resolvedPlaceName && placeByIdData?.placeById?.__typename === 'Place') {
      setResolvedPlaceName(placeByIdData.placeById.name);
    }
  }, [placeByIdData, resolvedPlaceName]);

  // Track whether geometry has been resolved for the current placeId.
  // Reset when placeId changes so a new destination triggers re-resolution.
  const hasResolvedGeoRef = React.useRef(false);
  const resolvedPlaceIdRef = React.useRef(placeId);
  React.useEffect(() => {
    if (resolvedPlaceIdRef.current !== placeId) {
      resolvedPlaceIdRef.current = placeId;
      hasResolvedGeoRef.current = false;
    }
  }, [placeId]);

  // Log initial place resolution errors to Sentry. When geometry resolution fails, the listing
  // search stays skipped and isGeoResolutionError surfaces the failure to the UI.
  React.useEffect(() => {
    if (placeByIdError && shouldResolveGeometry) {
      Sentry.captureException(placeByIdError, {
        tags: {
          context: 'place-metadata-resolution',
          placeId: placeId || 'unknown'
        }
      });
    }
    if (placeSearchGeometryError && shouldResolveGeometry) {
      Sentry.captureException(placeSearchGeometryError, {
        tags: {
          context: 'search-geometry-resolution',
          placeId: placeId || 'unknown'
        }
      });
    }
  }, [placeByIdError, placeSearchGeometryError, shouldResolveGeometry, placeId]);

  // Resolve search-specific geometry when no coordinates are in the URL.
  // This sets the initial geoSearchBox and centerPoint so the listing search
  // can fire with explicit Map coordinates from the start.
  React.useEffect(() => {
    const searchGeometry = placeSearchGeometryData?.placeSearchGeometry;
    if (!shouldResolveGeometry || hasResolvedGeoRef.current || placeByIdData?.placeById?.__typename !== 'Place' || !searchGeometry?.centerPoint || !searchGeometry.northEastPoint || !searchGeometry.southWestPoint) {
      return;
    }
    hasResolvedGeoRef.current = true;

    // Extract non-null search geometry points (TypeScript narrowing doesn't survive
    // through intermediate variable assignment, so we destructure directly
    // from the guarded values).
    const {
      centerPoint: cp,
      northEastPoint: ne,
      southWestPoint: sw
    } = searchGeometry;
    const centerPoint_ = cp!;
    const northEastPoint_ = ne!;
    const southWestPoint_ = sw!;
    const resolvedGeometry: Geometry = {
      centerPoint: {
        lat: centerPoint_.lat,
        lng: centerPoint_.lng
      },
      northEastPoint: {
        lat: northEastPoint_.lat,
        lng: northEastPoint_.lng
      },
      southWestPoint: {
        lat: southWestPoint_.lat,
        lng: southWestPoint_.lng
      }
    };
    _setMapGeoSearchBox(resolvedGeometry);
    setCenterPoint(resolvedGeometry.centerPoint);

    // Calculate zoom from raw Google Place types
    const rawTypes: string[] = (placeByIdData.placeById.rawTypes ?? []).flatMap((t: string | null) => t ? [t] : []);
    setMapZoom(calculateMapZoomFromRawTypes(rawTypes));

    // Sync resolved coordinates to URL so page refresh works
    syncStateToUrl({
      northEastLat: northEastPoint_.lat.toString(),
      northEastLng: northEastPoint_.lng.toString(),
      southWestLat: southWestPoint_.lat.toString(),
      southWestLng: southWestPoint_.lng.toString(),
      centerLat: centerPoint_.lat.toString(),
      centerLng: centerPoint_.lng.toString()
    });
  }, [placeByIdData, placeSearchGeometryData, shouldResolveGeometry]);
  const [minNumberOfRooms, setMinNumberOfRooms] = React.useState(initMinNumberOfRooms);
  const [minNumberOfBathrooms, setMinNumberOfBathrooms] = React.useState(initMinNumberOfBathrooms);
  const [minNumberOfBeds, setMinNumberOfBeds] = React.useState(initMinNumberOfBeds);
  const [minPrice, setMinPrice] = React.useState<number | null>(initMinPrice);
  const debouncedMinPrice = useDebounce(minPrice, 300);
  const [maxPrice, setMaxPrice] = React.useState<number | null>(initMaxPrice);
  const debouncedMaxPrice = useDebounce(maxPrice, 300);
  const [adultCount, setAdultCount] = React.useState(initAdultCount);
  const [childCount, setChildCount] = React.useState(initChildCount);
  const [infantCount, setInfantCount] = React.useState(initInfantCount);
  const [petCount, setPetCount] = React.useState(initPetCount);
  const [guestCountHumanReadableValue, setGuestCountHumanReadableValue] = React.useState('');
  const [checkInDate, setCheckInDate] = React.useState<string | null>(initCheckInDate);
  const [checkOutDate, setCheckOutDate] = React.useState<string | null>(initCheckOutDate);
  const [stayLength, setStayLength] = React.useState<number | null>(null);
  const {
    setListings,
    setSearch,
    emitSearchQueryResultsEvent
  } = useAnalytics();
  const lastReportedQueryRef = React.useRef<SearchQueryVariables | null>(null);
  const reportedQueryCountRef = React.useRef(0);
  const [activeFilters, _setActiveFilters] = React.useState<Feature[]>(allFeatures.filter(feature => initActiveFeatures.includes(feature.key)));

  // When allFeatures loads asynchronously (after mount), the useState
  // initializer has already resolved against an empty array. Detect when
  // allFeatures becomes available and synchronously update activeFilters
  // during render so downstream effects see the correct value.
  const hasResolvedInitFeaturesRef = React.useRef(initActiveFeatures.length === 0 || allFeatures.length > 0);
  if (!hasResolvedInitFeaturesRef.current && initActiveFeatures.length > 0 && allFeatures.length > 0) {
    hasResolvedInitFeaturesRef.current = true;
    _setActiveFilters(allFeatures.filter(feature => initActiveFeatures.includes(feature.key)));
  }
  const setActiveFilters = React.useCallback((features: Feature[]) => {
    syncStateToUrl({
      activeFeatures: features.map(feature => feature.key).join(',')
    });
    _setActiveFilters(features);
  }, []);
  const [propertyTypes, _setPropertyTypes] = React.useState<PropertyType[]>(initPropertyTypes);
  const setPropertyTypes = React.useCallback((nextPropertyTypes: PropertyType[]) => {
    syncStateToUrl({
      propertyTypes: nextPropertyTypes.join(',')
    });
    _setPropertyTypes(nextPropertyTypes);
  }, []);
  React.useEffect(() => {
    if (isSSR) return;
    syncStateToUrl({
      adultCount: adultCount.toString(),
      childCount: childCount.toString(),
      infantCount: infantCount.toString(),
      petCount: petCount.toString(),
      minNumberOfRooms: minNumberOfRooms.toString(),
      minNumberOfBathrooms: minNumberOfBathrooms.toString(),
      minNumberOfBeds: minNumberOfBeds.toString(),
      minPrice: minPrice ? minPrice.toString() : '',
      maxPrice: maxPrice ? maxPrice.toString() : '',
      checkInDate: checkInDate || '',
      checkOutDate: checkOutDate || '',
      pageSize: pageSize.toString()
    });
  }, [adultCount, childCount, infantCount, petCount, minNumberOfRooms, minNumberOfBathrooms, minNumberOfBeds, minPrice, maxPrice, checkInDate, checkOutDate, pageSize]);
  React.useEffect(() => {
    if (checkInDate && checkOutDate) {
      const nights = dayjs(checkOutDate).diff(dayjs(checkInDate), 'day');
      setStayLength(nights > 0 ? nights : null);
    } else {
      setStayLength(null);
    }
  }, [checkInDate, checkOutDate]);
  React.useEffect(() => {
    setGuestCountHumanReadableValue(guestDescription(adultCount, childCount, infantCount, petCount));
  }, [adultCount, childCount, infantCount, petCount]);
  const [minPriceCents, setMinPriceCents] = React.useState<number | undefined>(undefined);
  const [maxPriceCents, setMaxPriceCents] = React.useState<number | undefined>(undefined);
  React.useEffect(() => {
    setMinPriceCents(stayLength && debouncedMinPrice ? stayLength * Math.round(debouncedMinPrice * 100) : undefined);
    setMaxPriceCents(stayLength && debouncedMaxPrice ? stayLength * Math.round(debouncedMaxPrice * 100) : undefined);
  }, [debouncedMinPrice, debouncedMaxPrice, stayLength]);

  // ---------------------------------------------------------------------------
  // BHT-4019 — browser back restores prior search request.
  //
  // The default URL writer (`syncStateToUrl`) uses `replaceState`, which keeps
  // the URL live but never extends the history stack — so `Back` exits
  // `/search` entirely. Below we add two complementary effects:
  //
  //   1. A debounced commit effect that calls `pushState` once per ~400ms
  //      burst of navigable-state changes. Bursts coalesce so a single user
  //      interaction (e.g. opening the date picker and choosing two dates)
  //      yields one history entry, not five.
  //   2. A `popstate` listener that re-parses the URL and calls every setter
  //      to restore SearchProvider state, so Back/Forward produce the same
  //      results as a fresh page load with that URL.
  //
  // Pagination cursors, map-zoom-only writes, and initial search geometry
  // hydration intentionally remain on `replaceState` so they don't
  // pollute the history stack.
  const navigableKey = React.useMemo(() => JSON.stringify({
    placeId: placeId ?? null,
    adultCount,
    childCount,
    infantCount,
    petCount,
    minNumberOfRooms,
    minNumberOfBathrooms,
    minNumberOfBeds,
    minPrice,
    maxPrice,
    checkInDate,
    checkOutDate,
    activeFeatures: activeFilters.map(f => f.key).sort(),
    propertyTypes: [...propertyTypes].sort(),
    geoSearchBox
  }), [placeId, adultCount, childCount, infantCount, petCount, minNumberOfRooms, minNumberOfBathrooms, minNumberOfBeds, minPrice, maxPrice, checkInDate, checkOutDate, activeFilters, propertyTypes, geoSearchBox]);
  const lastCommittedUrlRef = React.useRef<string | null>(null);
  // Suppress commits until this timestamp (ms since epoch). Set to a future
  // time after popstate fires so the map's auto-resettle doesn't push a
  // phantom history entry.
  const popstateSettleUntilRef = React.useRef(0);

  // Hydration is complete when geometry is available (or wasn't needed). Only
  // then do we capture the baseline URL — otherwise the search geometry effect
  // would race the commit effect and either push a hydration entry or treat
  // the pre-hydration URL as the baseline.
  const isHydrated = !shouldResolveGeometry || !!geoSearchBox;
  React.useEffect(() => {
    if (isSSR) return;
    if (!isHydrated) return;
    const currentUrl = window.location.pathname + window.location.search;
    const isInPopstateSettle = Date.now() < popstateSettleUntilRef.current;
    if (lastCommittedUrlRef.current === null) {
      lastCommittedUrlRef.current = currentUrl;
      return;
    }

    // Async hydration paths (e.g. async features list resolving) can change
    // navigable state without changing the URL — never push for those.
    if (currentUrl === lastCommittedUrlRef.current) return;

    // We're inside the post-popstate settle window: the URL changes are the
    // map's auto-resettle, not user navigation. Slide the baseline forward
    // so the next genuine pan still differs from baseline and pushes.
    if (isInPopstateSettle) {
      lastCommittedUrlRef.current = currentUrl;
      return;
    }
    const timer = setTimeout(() => {
      const urlAtCommit = window.location.pathname + window.location.search;
      if (urlAtCommit === lastCommittedUrlRef.current) return;
      const priorUrl = lastCommittedUrlRef.current;
      lastCommittedUrlRef.current = urlAtCommit;
      // syncStateToUrl uses replaceState as state mutates, so the entry the
      // user is *on* has already been overwritten with `urlAtCommit` by the
      // time we get here. A bare pushState(urlAtCommit) would duplicate the
      // current entry and Back would be a visual no-op. Restore the prior
      // URL onto the current entry first, then push the new URL — that way
      // the previous state survives in the back-stack.
      if (priorUrl !== null) {
        window.history.replaceState(null, '', priorUrl);
      }
      window.history.pushState(null, '', urlAtCommit);
    }, COMMIT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [navigableKey, isHydrated]);

  // Re-bind `popstate` when `allFeatures` changes so the active-filter mapping
  // sees the latest feature list. Setters from `useState` are stable refs and
  // therefore don't need to be in deps.
  React.useEffect(() => {
    if (isSSR) return;
    const handlePopState = () => {
      const init = buildSearchInitParams(getDecodedSearchParams());
      setAdultCount(init.initAdultCount);
      setChildCount(init.initChildCount);
      setInfantCount(init.initInfantCount);
      setPetCount(init.initPetCount);
      setMinNumberOfRooms(init.initMinNumberOfRooms);
      setMinNumberOfBathrooms(init.initMinNumberOfBathrooms);
      setMinNumberOfBeds(init.initMinNumberOfBeds);
      setMinPrice(init.initMinPrice);
      setMaxPrice(init.initMaxPrice);
      setCheckInDate(init.initCheckInDate);
      setCheckOutDate(init.initCheckOutDate);
      _setPropertyTypes(init.initPropertyTypes);
      _setActiveFilters(allFeatures.filter(feature => init.initActiveFeatures.includes(feature.key)));
      // Only restore geometry when the prior URL actually carried coordinates.
      // Otherwise leave the resolved viewport alone — clearing it would unmount
      // the map's listing search until search geometry resolves again.
      if (init.initMapCoordinates) {
        _setMapGeoSearchBox(init.initMapCoordinates);
        setCenterPoint(init.initMapCoordinates.centerPoint);
      }
      if (init.initMapZoom !== undefined) {
        setMapZoom(init.initMapZoom);
      }
      setCursors({
        cursorAfter: init.cursorAfter ?? undefined,
        cursorBefore: init.cursorBefore ?? undefined
      });
      popstateSettleUntilRef.current = Date.now() + POPSTATE_SETTLE_MS;
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [allFeatures]);
  const currentSearchParams: SearchQueryParams = {
    accountId,
    placeId: placeId || DEFAULT_PLACE_ID,
    searchType,
    checkInDate: checkInDate || null,
    checkOutDate: checkOutDate || null,
    adultCount,
    childCount,
    infantCount,
    petCount,
    minNumberOfRooms,
    minNumberOfBathrooms,
    minNumberOfBeds,
    minPriceCents,
    maxPriceCents,
    featureKeys: activeFilters.map(feature => feature.key),
    propertyTypes,
    cursorAfter: cursors.cursorAfter,
    cursorBefore: cursors.cursorBefore,
    pageSize,
    northEastLat: accountId ? 90 : geoSearchBox?.northEastPoint.lat,
    northEastLng: accountId ? 180 : geoSearchBox?.northEastPoint.lng,
    southWestLat: accountId ? -90 : geoSearchBox?.southWestPoint.lat,
    southWestLng: accountId ? -180 : geoSearchBox?.southWestPoint.lng,
    centerLat: accountId ? 0 : geoSearchBox?.centerPoint.lat,
    centerLng: accountId ? 0 : geoSearchBox?.centerPoint.lng
  };
  const queryVariables = buildSearchQueryVariables(currentSearchParams);

  // Skip the listing search until geometry is available. When the URL has no
  // coordinates, we wait for search-specific place geometry first.
  // If geometry resolution fails, the search stays skipped
  // (isGeoResolutionError surfaces the failure to the UI).
  const isGeometryReady = !shouldResolveGeometry || !!geoSearchBox;
  const searchGeometry = placeSearchGeometryData?.placeSearchGeometry;
  const placeById = placeByIdData?.placeById;
  const hasUnusablePlaceResponse = !placeByIdError && !!placeById && placeById.__typename === 'NotFoundError';
  const hasUnusableSearchGeometryResponse = !placeSearchGeometryError && !!placeSearchGeometryData && (!searchGeometry?.centerPoint || !searchGeometry.northEastPoint || !searchGeometry.southWestPoint);
  const isGeoResolutionError = shouldResolveGeometry && !geoSearchBox && (!!placeByIdError || hasUnusablePlaceResponse || !!placeSearchGeometryError || hasUnusableSearchGeometryResponse);
  const {
    data: listingsQueryResults,
    loading: listingsLoading,
    error: listingsError,
    refetch: refetchListings
  } = useQuery(ListingsSearchDocument, {
    ssr: isSSR,
    notifyOnNetworkStatusChange: true,
    variables: queryVariables,
    fetchPolicy: 'cache-first',
    skip: !isGeometryReady
  });

  // Log listing search errors to Sentry so they're tracked without
  // crashing the component tree.
  React.useEffect(() => {
    if (listingsError) {
      Sentry.captureException(listingsError, {
        tags: {
          context: 'listings-search'
        }
      });
    }
  }, [listingsError]);
  const retrySearch = React.useCallback(() => {
    refetchListings();
  }, [refetchListings]);
  const nextPage = React.useCallback(() => {
    if (listingsQueryResults?.listingsSearch.__typename === 'ListingSearchCursorCollection') {
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.delete('cursorBefore');
      if (listingsQueryResults.listingsSearch.metadata.cursorAfter) {
        queryParams.set('cursorAfter', listingsQueryResults.listingsSearch.metadata.cursorAfter);
        setCursors({
          cursorAfter: listingsQueryResults.listingsSearch.metadata.cursorAfter,
          cursorBefore: undefined
        });
      }
      history.replaceState(null, '', '?' + queryParams.toString());
    }
  }, [listingsQueryResults]);
  const prevPage = React.useCallback(() => {
    if (listingsQueryResults?.listingsSearch.__typename === 'ListingSearchCursorCollection') {
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.delete('cursorAfter');
      if (listingsQueryResults.listingsSearch.metadata.cursorBefore) {
        queryParams.set('cursorBefore', listingsQueryResults.listingsSearch.metadata.cursorBefore);
        setCursors({
          cursorAfter: undefined,
          cursorBefore: listingsQueryResults.listingsSearch.metadata.cursorBefore
        });
      }
      history.replaceState(null, '', '?' + queryParams.toString());
    }
  }, [listingsQueryResults]);
  React.useEffect(() => {
    if (listingsQueryResults?.listingsSearch.__typename === 'ListingSearchCursorCollection') {
      setSearch({
        id: uuid(),
        type: ListingsSearchType.Map,
        destination: resolvedPlaceName,
        guestCount: adultCount + childCount,
        checkInDate: checkInDate || undefined,
        checkOutDate: checkOutDate || undefined
      });
    } else {
      setSearch(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally fires only on new query results to avoid duplicate analytics events
  }, [listingsQueryResults]);
  React.useEffect(() => {
    if (listingsQueryResults?.listingsSearch?.__typename === 'ListingSearchCursorCollection') {
      setListings('search', listingsQueryResults.listingsSearch.entries);
    }
    // Don't fire view_item_list event with empty listings - only fire when we have data
  }, [listingsQueryResults]);

  // resolvedPlaceName is set by an effect one render after placeByIdData
  // lands, so read the response directly to avoid a render with neither.
  const searchDestination = resolvedPlaceName ?? (placeByIdData?.placeById?.__typename === 'Place' ? placeByIdData.placeById.name : null);
  const isSearchDestinationPending = !searchDestination && !!placeId && !placeByIdData && !placeByIdError;

  // Host account pages search one portfolio across the whole globe, which says
  // nothing about where travellers want to stay.
  React.useEffect(() => {
    if (accountId || isSearchDestinationPending || listingsQueryResults?.listingsSearch?.__typename !== 'ListingSearchCursorCollection') {
      return;
    }
    const lastReportedQuery = lastReportedQueryRef.current;
    if (lastReportedQuery && isSameSearchQuery(lastReportedQuery, queryVariables)) return;
    lastReportedQueryRef.current = queryVariables;
    reportedQueryCountRef.current += 1;
    emitSearchQueryResultsEvent({
      queryIndex: reportedQueryCountRef.current,
      resultCount: listingsQueryResults.listingsSearch.metadata.totalCount ?? null,
      destination: searchDestination,
      query: currentSearchParams
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Fires on new query results, or once the destination name they wait on arrives; the params read here are the ones that produced them
  }, [listingsQueryResults, isSearchDestinationPending]);
  const handlePetFriendlyToggle = React.useCallback((isChecked: boolean) => {
    if (isChecked) {
      const petFriendlyFeature = allFeatures.find(feature => feature.key === FeatureKey.PetFriendly);
      if (petFriendlyFeature && !activeFilters.some(filter => filter.key === FeatureKey.PetFriendly)) {
        setActiveFilters([...activeFilters, petFriendlyFeature]);
        setPetCount(1);
      }
    } else {
      setActiveFilters(activeFilters.filter(filter => filter.key !== FeatureKey.PetFriendly));
      setPetCount(0);
    }
  }, [allFeatures, activeFilters, setActiveFilters]);
  React.useEffect(() => {
    const petFriendlyFilter = activeFilters.find(filter => filter.key === FeatureKey.PetFriendly);
    if (petCount > 0 && !petFriendlyFilter) {
      const petFriendlyFeature = allFeatures.find(feature => feature.key === FeatureKey.PetFriendly);
      if (petFriendlyFeature) {
        setActiveFilters([...activeFilters, petFriendlyFeature]);
      }
    } else if (petCount === 0 && petFriendlyFilter) {
      setActiveFilters(activeFilters.filter(filter => filter.key !== FeatureKey.PetFriendly));
    }
  }, [petCount, activeFilters, allFeatures]);
  const filtersValue: SearchFiltersContextValue = React.useMemo(() => ({
    activeFilters,
    allFeatures,
    setActiveFilters,
    propertyTypes,
    setPropertyTypes,
    checkInDate,
    checkOutDate,
    setCheckInDate,
    setCheckOutDate,
    adultCount,
    setAdultCount,
    childCount,
    setChildCount,
    infantCount,
    setInfantCount,
    petCount,
    setPetCount,
    handlePetFriendlyToggle,
    minNumberOfRooms,
    setMinNumberOfRooms,
    minNumberOfBeds,
    setMinNumberOfBeds,
    minNumberOfBathrooms,
    setMinNumberOfBathrooms,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    guestCountHumanReadableValue
  }), [activeFilters, allFeatures, setActiveFilters, propertyTypes, setPropertyTypes, checkInDate, checkOutDate, adultCount, childCount, infantCount, petCount, handlePetFriendlyToggle, minNumberOfRooms, minNumberOfBeds, minNumberOfBathrooms, minPrice, maxPrice, guestCountHumanReadableValue]);
  const mapValue: SearchMapContextValue = React.useMemo(() => ({
    setMapGeoSearchBox,
    geoSearchBox,
    centerPoint,
    mapZoom,
    mapViewportManuallyChanged,
    markMapViewportAsManuallyChanged
  }), [setMapGeoSearchBox, geoSearchBox, centerPoint, mapZoom, mapViewportManuallyChanged, markMapViewportAsManuallyChanged]);
  const resultsValue: SearchResultsContextValue = React.useMemo(() => ({
    listingsQueryResults,
    listingsLoading,
    listingsError,
    retrySearch,
    isGeoResolutionError,
    resetPagination,
    nextPage,
    prevPage,
    placeId: placeId || null,
    placeName: resolvedPlaceName || null
  }), [listingsQueryResults, listingsLoading, listingsError, retrySearch, isGeoResolutionError, resetPagination, nextPage, prevPage, placeId, resolvedPlaceName]);
  const uiValue: SearchUIContextValue = React.useMemo(() => ({
    activateFeaturesModal,
    deactivateFeaturesModal,
    activateMobileSearchModal,
    deactivateMobileSearchModal,
    activateSearchNavForm,
    deactivateSearchNavForm,
    featuresModalActive,
    mobileSearchModalActive,
    searchNavFormActive
  }), [activateFeaturesModal, deactivateFeaturesModal, activateMobileSearchModal, deactivateMobileSearchModal, activateSearchNavForm, deactivateSearchNavForm, featuresModalActive, mobileSearchModalActive, searchNavFormActive]);
  return <SearchUIProvider value={uiValue}>
      <SearchMapProvider value={mapValue}>
        <SearchFiltersProvider value={filtersValue}>
          <SearchResultsProvider value={resultsValue}>
            {children}
          </SearchResultsProvider>
        </SearchFiltersProvider>
      </SearchMapProvider>
    </SearchUIProvider>;
};